/**
 * Auth Service
 *
 * Provides authentication functionality like login, logout, and session management.
 */

import { ApiService } from '../api/service';
import { apiClient } from '../api/client';
import { ApiResponse, AuthLoginRequest, AuthLoginResponseItem } from '../../../model';
import { configService } from '../config/service';

// 閒置時間上限由配置服務提供，預設為 30 分鐘

export class AuthService extends ApiService {
    // 用於跟踪閒置超時的定時器ID
    private inactivityTimer: NodeJS.Timeout | null = null;

    // 用於開始追蹤使用者活動
    startActivityTracking() {
        // 先清除現有的計時器
        this.resetInactivityTimer();
        
        // 設置事件監聽器跟踪用戶活動
        window.addEventListener('mousemove', this.resetInactivityTimer.bind(this));
        window.addEventListener('mousedown', this.resetInactivityTimer.bind(this));
        window.addEventListener('keypress', this.resetInactivityTimer.bind(this));
        window.addEventListener('touchmove', this.resetInactivityTimer.bind(this));
        window.addEventListener('scroll', this.resetInactivityTimer.bind(this));
        
        // 初始計時器
        this.resetInactivityTimer();
    }

    // 重設閒置計時器
    resetInactivityTimer() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
        }
        
        // 從配置服務獲取目前的閒置超時設置
        const inactivityTimeout = configService.getSessionTimeoutMs();
        
        // 設定新的閒置計時器
        this.inactivityTimer = setTimeout(() => {
            // 如果使用者已登入但閒置時間過長，自動登出
            if (this.isAuthenticated()) {
                console.log('自動登出：使用者閒置時間過長');
                
                // 在自動登出時，我們不需要透過 AuthContext 的完整登出邏輯
                // 將直接清除認證信息並轉到登入頁面
                localStorage.removeItem('auth_token');
                localStorage.removeItem('login_time');
                
                // 停止活動追蹤
                this.stopActivityTracking();
                
                // 在本地存儲自動登出狀態，以便登入頁面可以顯示適當的提示
                sessionStorage.setItem('auto_logout', 'true');
                
                // 重整頁面以確保使用者返回登入畫面
                window.location.href = '/login';
            }
        }, inactivityTimeout);
    }

    // 停止活動追蹤
    stopActivityTracking() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
        
        // 移除事件監聽器
        window.removeEventListener('mousemove', this.resetInactivityTimer.bind(this));
        window.removeEventListener('mousedown', this.resetInactivityTimer.bind(this));
        window.removeEventListener('keypress', this.resetInactivityTimer.bind(this));
        window.removeEventListener('touchmove', this.resetInactivityTimer.bind(this));
        window.removeEventListener('scroll', this.resetInactivityTimer.bind(this));
    }

    /**
     * Login with username and password
     */
    async login(request: AuthLoginRequest): Promise<AuthLoginResponseItem | null> {
        try {
            const response = await apiClient.post<ApiResponse<AuthLoginResponseItem>>(
                '/api/auth/login',
                request
            );

            const result = this.processResponse<AuthLoginResponseItem>(response);
            // If result is null, return null immediately
            if (!result) return null;

            // 以下只存儲令牌，不處理活動追蹤
            if (result.token) {
                localStorage.setItem('auth_token', result.token);
                
                // 存儲登入時間
                localStorage.setItem('login_time', Date.now().toString());
                localStorage.setItem('userAccount', result.userAccount);
                localStorage.setItem('officialCount', result.officialCount);
                localStorage.setItem('sponsorCount', result.sponsorCount);
            }

            return result;
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Logout the current user
     */
    logout(): void {
        // 只起到清除身份驗證信息的作用，不處理活動追蹤
        // 活動追蹤的結束已經移至 authContext 中處理
        localStorage.removeItem('auth_token');
        localStorage.removeItem('login_time');

        // 其他登出邏輯可以在此添加
        // 例如，如果需要，可以呼叫登出端點
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!localStorage.getItem('auth_token');
    }

    /**
     * Get the current auth token
     */
    getToken(): string | null {
        return localStorage.getItem('auth_token');
    }
}

// Export a singleton instance
export const authService = new AuthService();
